/**
 * VersionAPIAgent
 * 
 * Agent responsible for fetching version information from the API.
 * 
 * Skills:
 * - GET_VERSION: Fetch version from /api/version
 * - RETURN_SHA: Extract SHA from response
 * - CACHE_BUST: Add timestamp to prevent cached responses
 */

const VersionAPIAgent = {
  name: 'VersionAPIAgent',
  
  skills: {
    /**
     * CACHE_BUST
     * Appends timestamp to URL to bypass browser/CDN cache
     * @param {string} url - The URL to bust cache for
     * @returns {string} URL with cache-busting parameter
     */
    CACHE_BUST: (url) => {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}_t=${Date.now()}`;
    },
    
    /**
     * GET_VERSION
     * Fetches current version from the API endpoint
     * @returns {Promise<{version: string, shortVersion: string, timestamp: number}>}
     */
    GET_VERSION: async () => {
      try {
        const url = VersionAPIAgent.skills.CACHE_BUST('/api/version');
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Version API error: ${response.status}`);
        }
        
        const data = await response.json();
        return {
          success: true,
          data
        };
      } catch (error) {
        console.warn('[VersionAPIAgent] GET_VERSION failed:', error.message);
        return {
          success: false,
          error: error.message
        };
      }
    },
    
    /**
     * RETURN_SHA
     * Extracts the version SHA from API response
     * @param {object} apiResponse - Response from GET_VERSION
     * @returns {string|null} The version string or null
     */
    RETURN_SHA: (apiResponse) => {
      if (!apiResponse?.success || !apiResponse?.data?.version) {
        return null;
      }
      return apiResponse.data.version;
    }
  },
  
  /**
   * Execute a skill by name
   * @param {string} skillName - Name of the skill to execute
   * @param  {...any} args - Arguments for the skill
   * @returns {any} Result of skill execution
   */
  execute: async (skillName, ...args) => {
    const skill = VersionAPIAgent.skills[skillName];
    if (!skill) {
      throw new Error(`[VersionAPIAgent] Unknown skill: ${skillName}`);
    }
    return await skill(...args);
  }
};

export default VersionAPIAgent;
